import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";
import graphqlClient from "@/GQLClient";
import {
  type CoreNewsAuthorFragment,
  type CoreNewsAsAdminItemFragment,
  type CoreNewsItemFragment,
} from "@/graphql";
import {
  GetCoreNewsAsAdminDocument,
  GetCoreNewsAuthorsDocument,
  GetCoreNewsDocument,
  MarkReadDocument,
  SaveCoreNewsDocument,
  type CoreNewsInput,
} from "@/graphql/generated-types";
import type { FetchPolicy } from "@apollo/client";

const getCoreNewsAsAdmin = async (
  fetchPolicy: FetchPolicy = "no-cache",
): Promise<CoreNewsAsAdminItemFragment[]> => {
  const response = await graphqlClient.query({
    query: GetCoreNewsAsAdminDocument,
    fetchPolicy,
  });

  if (response.data?.coreNews?.getAsAdmin) {
    return response.data?.coreNews?.getAsAdmin as CoreNewsAsAdminItemFragment[];
  }

  throw new SentryGraphQLError("coreNews.getCoreNewsAsAdmin");
};

const getCoreNews = async (): Promise<CoreNewsItemFragment[]> => {
  const response = await graphqlClient.query({
    query: GetCoreNewsDocument,
    fetchPolicy: "network-only",
  });

  if (response.data?.coreNews?.get) {
    return response.data?.coreNews?.get as CoreNewsItemFragment[];
  }

  throw new SentryGraphQLError("coreNews.getCoreNews");
};

const getCoreNewsAsEmployee = async (
  fetchPolicy: FetchPolicy = "no-cache",
): Promise<CoreNewsItemFragment[]> => {
  const response = await graphqlClient.query({
    query: GetCoreNewsDocument,
    fetchPolicy,
  });

  if (response.data?.coreNews?.get) {
    return response.data?.coreNews?.get as CoreNewsItemFragment[];
  }

  throw new SentryGraphQLError("coreNews.getCoreNewsAsEmployee");
};

const getAuthors = async (
  fetchPolicy: FetchPolicy = "no-cache",
): Promise<CoreNewsAuthorFragment[]> => {
  const response = await graphqlClient.query({
    query: GetCoreNewsAuthorsDocument,
    fetchPolicy,
  });

  if (response.data?.coreNews?.getAuthors) {
    return response.data?.coreNews.getAuthors as CoreNewsAuthorFragment[];
  }

  throw new SentryGraphQLError("coreNews.getAuthors");
};

const save = async (coreNews: CoreNewsInput) => {
  const response = await graphqlClient.mutate({
    mutation: SaveCoreNewsDocument,
    variables: {
      coreNews,
    },
  });

  if (response.data?.coreNews?.save?.success) {
    return response;
  }

  throw new SentryGraphQLError("coreNews.save");
};

const markRead = async (id: string) => {
  const response = await graphqlClient.mutate({
    mutation: MarkReadDocument,
    variables: {
      id: id,
      isRead: true,
    },
  });

  if (response.data?.coreNews?.markRead?.success) {
    return response;
  }

  throw new SentryGraphQLError("coreNews.markRead");
};

export default {
  getCoreNewsAsAdmin,
  getCoreNews,
  getCoreNewsAsEmployee,
  getAuthors,
  save,
  markRead,
};
